import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft, Loader2, Play, Sparkles, ShieldAlert, Target, Wrench, BarChart2, Cpu, FlaskConical } from "lucide-react";
import { toast } from "sonner";

const Section = ({ title, children }) => (
  <div className="m-card p-5"><p className="mono-label mb-3">{title}</p>{children}</div>
);
const Bullets = ({ items }) => (
  <ul className="space-y-2">{(items || []).map((x, i) => (
    <li key={i} className="text-sm text-[#E2E8F0] flex gap-2"><span className="text-[#2563EB] mt-0.5">›</span>{typeof x === "string" ? x : JSON.stringify(x)}</li>
  ))}</ul>
);

const TABS = [
  ["overview", "ag.tabOverview"], ["jobdesk", "ag.tabJobdesk"], ["skills", "ag.tabSkills"], ["sop", "ag.tabSop"],
  ["authority", "ag.tabAuthority"], ["tools", "ag.tabTools"], ["kpis", "ag.tabKpis"], ["runs", "ag.tabRuns"],
];

export default function AgentDetail() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [a, setA] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { api.agent(code).then(setA); }, [code]);

  const runTest = async () => {
    if (!prompt.trim()) return;
    setTesting(true); setResult(null);
    try {
      const res = await api.agentTest(code, { project_code: "P001", prompt });
      setResult(res);
    } catch { toast.error(t("ag.testFailed")); } finally { setTesting(false); }
  };

  if (!a) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#2563EB]" /></div>;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/workforce")} className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1.5" data-testid="agent-back"><ArrowLeft size={15} /> {t("ag.back")}</button>

      <div className="m-card p-6 flex flex-col sm:flex-row sm:items-center gap-5" style={{ background: "linear-gradient(135deg,#0F1626,#101d33)" }}>
        <div className="h-16 w-16 rounded-2xl bg-[#182238] border border-[#1E293B] flex items-center justify-center font-display font-extrabold text-white text-2xl">{a.name[0]}</div>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap"><h1 className="font-display text-2xl font-extrabold text-white">{a.name}</h1><span className="mono-label">{a.code}</span></div>
          <p className="text-[#E2E8F0]">{a.position}</p>
          <div className="flex items-center gap-2 mt-2 flex-wrap">
            <StatusPill status={a.availability_status} />
            <span className="text-xs text-[#64748B]">{a.employment_type?.replace(/_/g," ")}</span>
            {a.manager && <span className="text-xs text-[#64748B]">{t("ag.reportsTo", { name: a.manager.name, pos: a.manager.position })}</span>}
            <StatusPill status="UNRATED" label={t("ag.performance", { v: a.performance_rating })} />
          </div>
        </div>
        <button onClick={() => setTestOpen(true)} className="btn-primary h-11 px-5 flex items-center gap-2 text-sm" data-testid="agent-run-test"><FlaskConical size={16} /> {t("ag.runTest")}</button>
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="bg-[#0F1626] border border-[#1E293B] flex-wrap h-auto">
          {TABS.map(([k, lbl]) => (
            <TabsTrigger key={k} value={k} className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white text-[#94A3B8]" data-testid={`agent-tab-${k}`}>{t(lbl)}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Section title={t("ag.missionStatement")}><p className="text-sm text-[#E2E8F0] leading-relaxed">{a.mission_statement}</p></Section>
          <div className="grid sm:grid-cols-2 gap-4">
            <Section title={t("ag.specTags")}><div className="flex flex-wrap gap-2">{a.specialization_tags?.map((s) => <span key={s} className="m-pill text-[#60A5FA]" style={{borderColor:"#1E40AF",background:"#172554"}}>{s}</span>)}</div></Section>
            <Section title={t("ag.modelCapability")}><p className="text-sm text-white font-mono">{a.preferred_model_capabilities?.provider} · {a.preferred_model_capabilities?.model}</p><p className="text-xs text-[#64748B] mt-1">reasoning: {a.preferred_model_capabilities?.reasoning}</p></Section>
          </div>
          <Section title={t("ag.knowledgeScope")}><p className="text-sm text-[#E2E8F0]">{a.knowledge_scope}</p></Section>
        </TabsContent>

        <TabsContent value="jobdesk" className="mt-4 space-y-4">
          <Section title={t("ag.coreResp")}><Bullets items={a.responsibilities} /></Section>
          <Section title={t("ag.collabRules")}><Bullets items={a.collaboration_rules} /></Section>
          <Section title={t("ag.failureConds")}><Bullets items={a.failure_conditions} /></Section>
        </TabsContent>

        <TabsContent value="skills" className="mt-4">
          <Section title={t("ag.skillMatrix")}>
            <div className="space-y-3">
              {a.skill_rows?.map((s) => (
                <div key={s.skill} className="flex items-center gap-3">
                  <span className="text-sm text-[#E2E8F0] w-52 truncate">{s.skill.replace(/_/g," ")}</span>
                  <div className="flex-1 h-2 rounded-full bg-[#0B0F19] overflow-hidden"><div style={{ width: `${(s.level/5)*100}%` }} className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#00E5FF]" /></div>
                  <span className="text-xs text-white font-mono w-24 text-right">L{s.level} · {s.label}</span>
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="sop" className="mt-4"><Section title={t("ag.sop")}><ol className="space-y-2 list-decimal list-inside text-sm text-[#E2E8F0]">{a.sop?.map((s,i)=><li key={i}>{s}</li>)}</ol></Section></TabsContent>

        <TabsContent value="authority" className="mt-4 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Section title={t("ag.autonomy")}><p className="text-sm text-white flex items-center gap-2"><Target size={15} className="text-[#2563EB]" /> {a.autonomy_level}</p></Section>
            <Section title={t("ag.riskCeiling")}><StatusPill status={a.risk_ceiling} label={a.risk_ceiling} /></Section>
          </div>
          <Section title={t("ag.allowed")}><Bullets items={a.allowed_actions} /></Section>
          <div className="m-card p-5" style={{borderColor:"#7f1d1d"}}><p className="mono-label mb-3 text-[#FCA5A5] flex items-center gap-2"><ShieldAlert size={14}/> {t("ag.forbidden")}</p><Bullets items={a.forbidden_actions} /></div>
          <Section title={t("ag.approvalRules")}><Bullets items={a.approval_rules} /></Section>
          <Section title={t("ag.escalationRules")}><Bullets items={a.escalation_rules} /></Section>
        </TabsContent>

        <TabsContent value="tools" className="mt-4"><Section title={t("ag.toolPerms")}><div className="flex flex-wrap gap-2">{a.tool_permissions?.map((tp)=><span key={tp} className="m-pill text-[#34D399]" style={{borderColor:"#047857",background:"#064E3B"}}><Wrench size={12}/>{tp}</span>)}</div></Section></TabsContent>

        <TabsContent value="kpis" className="mt-4"><Section title={t("ag.kpis")}><div className="grid sm:grid-cols-3 gap-3">{a.kpis?.map((k)=><div key={k} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-4 flex items-center gap-2"><BarChart2 size={15} className="text-[#2563EB]"/><span className="text-sm text-[#E2E8F0]">{k}</span></div>)}</div></Section></TabsContent>

        <TabsContent value="runs" className="mt-4">
          <Section title={t("ag.aiRuns")}>
            {a.runs?.length ? (
              <div className="space-y-2">{a.runs.map((r)=>(
                <div key={r.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-lg px-3 py-2 flex items-center justify-between text-xs">
                  <span className="font-mono text-[#94A3B8]">{r.model} · {r.input_tokens}+{r.output_tokens} tok · {r.latency}s</span>
                  <span className="font-mono text-[#2563EB]">${r.model_cost}</span>
                </div>
              ))}</div>
            ) : <p className="text-sm text-[#64748B]">{t("ag.noRuns")}</p>}
          </Section>
        </TabsContent>
      </Tabs>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="bg-[#0F1626] border-[#1E293B] text-[#E2E8F0] max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-white flex items-center gap-2"><FlaskConical size={17} className="text-[#2563EB]"/> {t("ag.testMode", { name: a.name })}</DialogTitle></DialogHeader>
          <p className="text-xs text-[#64748B]">{t("ag.testContext", { m: a.preferred_model_capabilities?.model })}</p>
          <textarea value={prompt} onChange={(e)=>setPrompt(e.target.value)} rows={3} placeholder={t("ag.instructPh", { name: a.name })} className="input-dark w-full p-3 text-sm" data-testid="agent-test-prompt" />
          <button onClick={runTest} disabled={testing} className="btn-primary h-10 px-4 text-sm flex items-center gap-2 self-start disabled:opacity-60" data-testid="agent-test-submit">{testing ? <Loader2 size={15} className="animate-spin"/> : <Play size={15}/>} {t("ag.run")}</button>
          {result && (
            <div className="space-y-3 max-h-[45vh] overflow-y-auto">
              <div className="flex items-center gap-2 text-xs"><Cpu size={13} className="text-[#2563EB]"/><span className="font-mono text-[#94A3B8]">{result.provider} · {result.model} · ${result.cost}</span></div>
              <div className="m-card p-4"><p className="mono-label mb-2 flex items-center gap-1.5"><Sparkles size={12} className="text-[#2563EB]"/> Output</p><p className="text-sm text-[#E2E8F0] whitespace-pre-wrap">{result.output}</p></div>
              <details className="text-xs text-[#64748B]"><summary className="cursor-pointer">{t("ag.contextUsed")}</summary><pre className="whitespace-pre-wrap mt-2 font-mono text-[10px]">{result.context_used}</pre></details>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
